import { MutableRefObject, RefObject, useCallback, useEffect, useState } from 'react';
import Address from '../../models/Address';
import AddressMapResult from '../../models/AddressMapResult';
import {
  extendBoundsForAddresses,
  getAddressIdsKey,
  getInitialMapCenter,
  resetMapView,
  shouldFitBounds,
  isContainerMeasurable,
} from './multiMapUtils';

interface StreetViewState {
  position: google.maps.LatLngLiteral;
  pov: google.maps.StreetViewPov;
}

interface MapState {
  center: google.maps.LatLngLiteral;
  zoom: number;
  mapTypeId: google.maps.MapTypeId;
}

export function useMultiMapLoad(
  addresses: Address[] | AddressMapResult[],
  zoomOnSingle: number,
  containerRef: RefObject<HTMLDivElement | null>,
  mapRef: MutableRefObject<google.maps.Map | null>,
  fullscreen: boolean,
) {
  const addressIdsKey = getAddressIdsKey(addresses);

  const [mapReady, setMapReady] = useState(false);
  const [mapState, setMapState] = useState<MapState>({
    center: getInitialMapCenter(addresses),
    zoom: zoomOnSingle,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
  });
  const [streetViewActive, setStreetViewActive] = useState(false);
  const [streetViewState, setStreetViewState] = useState<StreetViewState | null>(null);

  useEffect(() => {
    setMapReady(false);
    mapRef.current = null;

    return () => {
      mapRef.current = null;
      setMapReady(false);
    };
  }, [addresses.length, addressIdsKey, mapRef]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      if (!isContainerMeasurable(containerRef.current)) {
        console.warn('Container not ready when onLoad called');
        return;
      }

      if (mapRef.current && mapRef.current !== map) {
        try {
          google.maps.event.clearInstanceListeners(mapRef.current);
        } catch {
          // Ignore cleanup errors
        }
      }

      mapRef.current = map;
      setMapReady(true);

      setTimeout(() => {
        if (
          !mapRef.current ||
          mapRef.current !== map ||
          !isContainerMeasurable(containerRef.current)
        ) {
          return;
        }

        const panorama = map.getStreetView();
        if (!panorama) {
          console.warn('StreetView panorama not available');
          return;
        }

        panorama.addListener('visible_changed', () => {
          if (!mapRef.current || mapRef.current !== map) {
            return;
          }

          const isVisible = panorama.getVisible();
          setStreetViewActive(isVisible);

          if (isVisible) {
            const position = panorama.getPosition()?.toJSON();
            const pov = panorama.getPov();
            if (position) {
              setStreetViewState({ position, pov });
            }
          }
        });
      }, 100);

      if (addresses.length === 0) {
        return;
      }

      const bounds = new window.google.maps.LatLngBounds();
      const { diagonalKm, nearbyPlacesPresent } = extendBoundsForAddresses(bounds, addresses);
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      if (!northEast || !southWest) {
        console.warn('Bounds are empty, cannot calculate zoom');
        return;
      }

      const performZoom = () => {
        if (shouldFitBounds(nearbyPlacesPresent, diagonalKm, addresses.length)) {
          map.fitBounds(bounds, {
            top: 30,
            bottom: 30,
            left: 30,
            right: 30,
          });
        } else {
          map.setCenter(bounds.getCenter());
          map.setZoom(zoomOnSingle);
        }
      };

      google.maps.event.addListenerOnce(map, 'tilesloaded', performZoom);

      map.addListener('idle', () => {
        setMapState({
          center: map.getCenter()?.toJSON() || mapState.center,
          zoom: map.getZoom() || zoomOnSingle,
          mapTypeId: map.getMapTypeId()! as google.maps.MapTypeId,
        });
      });
    },
    [addresses, zoomOnSingle, mapState.center, containerRef, mapRef],
  );

  useEffect(() => {
    if (!mapReady || !mapRef.current) {
      return;
    }

    const map = mapRef.current;
    const panorama = map.getStreetView();

    if (!panorama) {
      console.warn('StreetView panorama not available');
      setMapReady(false);
      return;
    }

    if (streetViewActive && streetViewState) {
      panorama.setPosition(streetViewState.position);
      panorama.setPov(streetViewState.pov);
      panorama.setVisible(true);
    } else {
      panorama.setVisible(false);
    }

    setMapReady(false);
  }, [fullscreen, mapReady, streetViewActive, streetViewState, mapRef]);

  const resetMap = useCallback(() => {
    if (!mapRef.current) {
      return;
    }

    resetMapView(mapRef.current, addresses, zoomOnSingle);
  }, [addresses, zoomOnSingle, mapRef]);

  return {
    mapState,
    onLoad,
    resetMap,
  };
}
